import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { listBounces, recipientStats } from "@/lib/recipient-tags.functions";
import { Skull } from "lucide-react";

export const Route = createFileRoute("/_authenticated/bounces")({
  component: BouncesPage,
  head: () => ({ meta: [{ title: "Bounces — InboxPulse" }] }),
});

function BouncesPage() {
  const [days, setDays] = useState<7 | 30 | 90>(30);
  const [bounceType, setBounceType] = useState<"all" | "hard" | "soft" | "block">("all");
  const [search, setSearch] = useState("");

  const fetchBounces = useServerFn(listBounces);
  const fetchStats = useServerFn(recipientStats);

  const { data: stats } = useQuery({
    queryKey: ["recipient-stats"],
    queryFn: () => fetchStats(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["bounces", days, bounceType, search],
    queryFn: () =>
      fetchBounces({
        data: {
          days,
          bounce_type: bounceType === "all" ? undefined : bounceType,
          search: search || undefined,
        },
      }),
  });

  const bounces = data?.bounces ?? [];

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Bounces"
        description="Welche Empfänger-Adressen haben gebounct – und warum? Verknüpft mit dem Tag, der in deiner Mail eingebaut war."
      />

      <HelpPanel
        title="Hilfe: Welche Mails kamen nicht an?"
        what={
          <>
            Liste aller <b>Empfänger, bei denen die Mail abgelehnt wurde</b>. Jeder Eintrag zeigt:
            wer, warum, wann.
          </>
        }
        why={
          <>
            Tote Adressen schaden deiner Reputation. Wer regelmäßig aussortiert, kommt bei den
            anderen besser an.
          </>
        }
        how={[
          <>Liste durchsehen — rote Einträge = harte Bounces (Adresse existiert nicht).</>,
          <>Auf eine Zeile klicken → genauer Grund (z.B. "User unknown", "Mailbox full").</>,
          <>Harte Bounces solltest du aus deiner Empfänger-Liste entfernen.</>,
        ]}
        tips={[
          <>Über 2% Bounce-Rate = Warnsignal. Über 5% = Versand sofort pausieren.</>,
          <>Weiche Bounces (z.B. voll) nochmal in 1-2 Tagen probieren.</>,
        ]}
        glossary={[
          { term: "Harter Bounce", def: "Adresse gibt es nicht. Nie wieder anschreiben." },
          {
            term: "Weicher Bounce",
            def: "Vorübergehendes Problem (Postfach voll, Server down). Später nochmal versuchen.",
          },
          {
            term: "SMTP-Code",
            def: "Fehlernummer vom Mail-Server, z.B. 550 = abgelehnt, 421 = später nochmal.",
          },
        ]}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-4 bg-card/60 border-border">
          <div className="text-xs uppercase text-muted-foreground tracking-wider">
            Verfolgte Empfänger
          </div>
          <div className="text-2xl font-semibold mt-1">{stats?.total ?? 0}</div>
        </Card>
        <Card className="p-4 bg-card/60 border-border">
          <div className="text-xs uppercase text-muted-foreground tracking-wider">
            Bounces gesamt
          </div>
          <div className="text-2xl font-semibold mt-1 text-destructive">{stats?.bounced ?? 0}</div>
        </Card>
        <Card className="p-4 bg-card/60 border-border">
          <div className="text-xs uppercase text-muted-foreground tracking-wider">Letzte 24 h</div>
          <div className="text-2xl font-semibold mt-1 text-warning">{stats?.bounced_24h ?? 0}</div>
        </Card>
      </div>

      <Card className="p-4 bg-card/60 border-border">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Zeitraum</Label>
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v) as 7 | 30 | 90)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Letzte 7 Tage</SelectItem>
                <SelectItem value="30">Letzte 30 Tage</SelectItem>
                <SelectItem value="90">Letzte 90 Tage</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Bounce-Typ</Label>
            <Select value={bounceType} onValueChange={(v) => setBounceType(v as typeof bounceType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
                <SelectItem value="soft">Soft</SelectItem>
                <SelectItem value="block">Block</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Empfänger suchen</Label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="z.B. anna@"
            />
          </div>
        </div>
      </Card>

      <Card className="bg-card/60 border-border overflow-hidden">
        {isLoading ? (
          <div className="p-10 text-center text-muted-foreground text-sm">Lade …</div>
        ) : bounces.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">
            <Skull className="size-8 mx-auto mb-2 opacity-60" />
            <p className="text-sm">Keine Bounces im gewählten Zeitraum.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-muted-foreground text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 text-left">Wann</th>
                  <th className="px-4 py-2 text-left">Empfänger</th>
                  <th className="px-4 py-2 text-left">Absender</th>
                  <th className="px-4 py-2 text-left">Tag</th>
                  <th className="px-4 py-2 text-left">Typ</th>
                  <th className="px-4 py-2 text-left">Grund</th>
                </tr>
              </thead>
              <tbody>
                {bounces.map((b) => (
                  <tr key={b.id} className="border-t border-border">
                    <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                      {b.bounced_at ? new Date(b.bounced_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-2 font-medium">{b.recipient_email}</td>
                    <td className="px-4 py-2 text-muted-foreground">{b.sender_email ?? "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{b.tag}</td>
                    <td className="px-4 py-2">
                      <Badge
                        variant={
                          b.bounce_type === "hard"
                            ? "destructive"
                            : b.bounce_type === "block"
                              ? "destructive"
                              : "secondary"
                        }
                      >
                        {b.bounce_type ?? "—"}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground max-w-[480px] truncate">
                      {b.bounce_reason ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
